import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert, Button, Icon, message, Select, Table, Tag, Tooltip
} from 'antd';
import moment from 'moment';
import { Layout } from 'components/Page';
import settings from 'settings';
import {
  downloadCsv as downloadCsvAction,
  loadPublishAttempts as loadPublishAttemptsAction,
  loadSummary as loadSummaryAction,
  loadViolations as loadViolationsAction,
  loadWritebackHistory as loadWritebackHistoryAction,
  retryPublish as retryPublishAction,
  setFilters as setFiltersAction
} from 'actions/optimiserStatus';
import { loadBrokerHealth as loadBrokerHealthAction } from 'actions/solaceHealth';
import { loadRunStatus as loadRunStatusAction, loadRunHistory as loadRunHistoryAction } from 'actions/optimiserRunStatus';
import { effectiveRunState } from 'components/OptimiserStatusBadge';
import OptimiserRunIcon from 'components/OptimiserStatusBadge/OptimiserRunIcon';
import {
  ERROR_TYPE_OPTIONS,
  RECORD_TYPE_OPTIONS,
  brokerStateLabel,
  errorTypeLabel,
  recordTypeLabel,
  runResultLabel,
  runStateLabel,
  writebackStateLabel
} from './config';
import StatusTile from './StatusTile';
import CollapsibleSection from './CollapsibleSection';
import ConnectedRecordDetailHost from './RecordDetailHost';
import ConnectedPublishAttemptDetail from './PublishAttemptDetail';
import './style.scss';

const CROLogo = require('assets/images/logo/CRO Logo.png');

const { Option } = Select;
const { APP_CONFIG } = settings;
const PAGE_SIZE = APP_CONFIG.defaultTablePageSize;

// Column builders live at module scope and take the callbacks they need rather
// than reaching into instance state, which keeps them simple to read and test.
export const buildSummaryColumns = onDrillDown => [
  {
    title: 'Record Type',
    dataIndex: 'record_type',
    key: 'record_type',
    render: text => recordTypeLabel(text),
    sorter: (a, b) => recordTypeLabel(a.record_type).localeCompare(recordTypeLabel(b.record_type))
  },
  {
    title: 'Error Type',
    dataIndex: 'error_type',
    key: 'error_type',
    render: text => errorTypeLabel(text),
    sorter: (a, b) => errorTypeLabel(a.error_type).localeCompare(errorTypeLabel(b.error_type))
  },
  {
    title: 'Failing Records',
    dataIndex: 'count',
    key: 'count',
    sorter: (a, b) => a.count - b.count
  },
  {
    title: '',
    key: 'action',
    width: '90px',
    render: (text, record) => (
      <Button size="small" onClick={() => onDrillDown(record)}>
        View
      </Button>
    )
  }
];

export const buildViolationColumns = () => [
  {
    title: 'Record Type',
    dataIndex: 'record_type',
    key: 'record_type',
    render: text => recordTypeLabel(text),
    sorter: true
  },
  {
    title: 'Record',
    dataIndex: 'record_label',
    key: 'record_label',
    render: (text, record) => record.record_label || record.record_id,
    sorter: true
  },
  {
    title: 'Error Type',
    dataIndex: 'error_type',
    key: 'error_type',
    render: text => <Tag>{errorTypeLabel(text)}</Tag>,
    sorter: true
  },
  {
    title: 'Detail',
    dataIndex: 'detail',
    key: 'detail',
    sorter: true
  }
];

export const buildWarningColumns = () => [
  {
    title: 'Record Type',
    dataIndex: 'record_type',
    key: 'record_type',
    render: text => recordTypeLabel(text)
  },
  {
    title: 'Record',
    dataIndex: 'record_label',
    key: 'record_label',
    render: (text, record) => record.record_label || record.record_id
  },
  {
    title: 'Expiring Tags',
    dataIndex: 'details',
    key: 'details',
    render: (details) => {
      const items = details || [];
      return items.map(d => `${d.collection}: ${d.tag_value} (expires ${moment(d.expiry_date).format('DD MMM YYYY HH:mm')})`).join(', ');
    }
  }
];

// A violation row is uniquely identified by the record PLUS the specific contract
// rule it failed (error_type + detail). A single record can fail several rules of
// the SAME error_type - e.g. a user missing the Depot, Is-a-mentor and Position
// tags all surface as `missing_tag` - so `detail` MUST be part of the key. Without
// it the rows collide on a duplicate React key, and an in-place refresh leaves
// stale rows in the table (only a full page reload rebuilds the tree correctly).
export const violationRowKey = record => `${record.record_type}-${record.record_id}-${record.error_type}-${record.detail}`;

export const buildAttemptColumns = (onRetry, retryingId) => [
  {
    title: 'Time',
    dataIndex: 'created_date',
    key: 'created_date',
    render: text => moment(text).format('DD MMM YYYY HH:mm')
  },
  {
    title: 'Record type',
    dataIndex: 'record_type',
    key: 'record_type',
    render: text => recordTypeLabel(text)
  },
  { title: 'Record', dataIndex: 'record_title', key: 'record_title' },
  {
    title: 'Status',
    dataIndex: 'succeeded',
    key: 'succeeded',
    render: ok => <Tag color={ok ? 'green' : 'red'}>{ok ? 'Success' : 'Failure'}</Tag>
  },
  {
    title: '',
    key: 'retry',
    width: '90px',
    render: (text, record) => (record.can_retry
      ? (
        <Button
          size="small"
          loading={retryingId === record.id}
          disabled={retryingId !== null}
          onClick={(event) => {
            event.stopPropagation();
            onRetry(record.id);
          }}
        >
          Retry
        </Button>
      )
      : null)
  }
];

export const buildRunHistoryColumns = () => [
  {
    title: 'Time',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    render: text => (text ? moment(text).format('DD MMM YYYY HH:mm') : '—')
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    render: (result) => {
      const meta = runResultLabel(result);
      return <Tag color={meta.tag}>{meta.label}</Tag>;
    }
  },
  {
    title: 'Date from',
    dataIndex: 'fromDate',
    key: 'fromDate',
    render: text => (text ? moment(text).format('DD MMM YYYY') : '—')
  },
  {
    title: 'Date to',
    dataIndex: 'toDate',
    key: 'toDate',
    render: text => (text ? moment(text).format('DD MMM YYYY') : '—')
  },
  { title: 'Message', dataIndex: 'message', key: 'message' }
];

export const appliedSummary = row => (
  `${row.createdCount} new · ${row.updatedCount} changed · ${row.deletedCount} removed`
);

export const buildWritebackColumns = () => [
  {
    title: 'Time',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    render: text => (text ? moment(text).format('DD MMM YYYY HH:mm') : '—')
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status) => {
      const meta = writebackStateLabel(status);
      return meta.tag ? <Tag color={meta.tag}>{meta.label}</Tag> : meta.label;
    }
  },
  {
    title: 'Applied',
    key: 'applied',
    render: (text, row) => (row.status === 'applied' ? appliedSummary(row) : '—')
  },
  {
    title: 'SMS',
    key: 'sms',
    render: (text, row) => ((row.smsEnqueuedCount || row.smsSkippedCount)
      ? `${row.smsEnqueuedCount} sent / ${row.smsSkippedCount} skipped`
      : '—')
  },
  {
    title: 'Run', dataIndex: 'runId', key: 'runId', render: text => text || '—'
  }
];

export class OptimiserStatusPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 0, sort: {}, selectedRecord: null, selectedAttemptId: null, retryingId: null, showOlderFailures: false
    };
    this.handleDrillDown = this.handleDrillDown.bind(this);
    this.handleRecordTypeChange = this.handleRecordTypeChange.bind(this);
    this.handleErrorTypeChange = this.handleErrorTypeChange.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
    this.handleTableChange = this.handleTableChange.bind(this);
    this.handleDownload = this.handleDownload.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRecordSaved = this.handleRecordSaved.bind(this);
    this.handleRecordClose = this.handleRecordClose.bind(this);
    this.handleAttemptClick = this.handleAttemptClick.bind(this);
    this.handleAttemptClose = this.handleAttemptClose.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
    this.handleToggleOlderFailures = this.handleToggleOlderFailures.bind(this);
  }

  handleToggleOlderFailures() {
    this.setState(prev => ({ showOlderFailures: !prev.showOlderFailures }));
  }

  componentDidMount() {
    const {
      loadBrokerHealth, loadPublishAttempts, loadSummary, loadViolations, loadRunStatus, loadRunHistory,
      loadWritebackHistory, filters
    } = this.props;
    loadSummary();
    loadPublishAttempts();
    loadBrokerHealth();
    loadRunStatus();
    loadRunHistory();
    loadWritebackHistory();
    this.pollId = setInterval(() => {
      loadBrokerHealth();
      loadRunStatus();
      loadRunHistory();
      loadWritebackHistory();
    }, 60000);
    loadViolations({
      page: 0, pageSize: PAGE_SIZE, ...filters, sort: {}
    });
  }

  componentWillUnmount() {
    if (this.pollId) clearInterval(this.pollId);
  }

  reload(filters, page, sort = {}) {
    const { loadViolations } = this.props;
    this.setState({ page });
    loadViolations({
      page, pageSize: PAGE_SIZE, ...filters, sort
    });
  }

  handleDrillDown(row) {
    const { setFilters } = this.props;
    const filters = { recordType: row.record_type, errorType: row.error_type };
    setFilters(filters);
    this.reload(filters, 0);
  }

  handleRecordTypeChange(value) {
    const { setFilters, filters } = this.props;
    const next = { ...filters, recordType: value || null };
    setFilters({ recordType: value || null });
    this.reload(next, 0);
  }

  handleErrorTypeChange(value) {
    const { setFilters, filters } = this.props;
    const next = { ...filters, errorType: value || null };
    setFilters({ errorType: value || null });
    this.reload(next, 0);
  }

  handleClearFilters() {
    const { setFilters } = this.props;
    const cleared = { recordType: null, errorType: null };
    setFilters(cleared);
    this.reload(cleared, 0);
  }

  handleTableChange(pagination, _filters, sorter) {
    const { filters } = this.props;
    const page = (pagination.current || 1) - 1;
    const sort = sorter && sorter.field ? { field: sorter.field, order: sorter.order } : {};
    this.setState({ sort });
    this.reload(filters, page, sort);
  }

  handleRowClick(row) {
    this.setState({
      selectedRecord: { recordType: row.record_type, recordId: row.record_id },
      selectedAttemptId: null
    });
  }

  handleAttemptClick(row) {
    this.setState({ selectedAttemptId: row.id, selectedRecord: null });
  }

  handleAttemptClose() {
    this.setState({ selectedAttemptId: null });
  }

  handleRecordSaved() {
    const { loadSummary, loadViolations, filters } = this.props;
    const { page, sort } = this.state;
    loadSummary();
    loadViolations({
      page, pageSize: PAGE_SIZE, ...filters, sort
    });
  }

  handleRecordClose() {
    this.setState({ selectedRecord: null });
  }

  handleDownload() {
    const { downloadCsv, filters } = this.props;
    downloadCsv(filters);
  }

  handleRefresh() {
    const { loadSummary, filters } = this.props;
    this.setState({ sort: {} });
    loadSummary();
    this.reload(filters, 0);
  }

  handleRetry(id) {
    const { retryPublish, loadPublishAttempts } = this.props;
    this.setState({ retryingId: id });
    return retryPublish(id)
      .then(() => {
        this.setState({ retryingId: null });
        message.success('Retry queued.');
        setTimeout(() => loadPublishAttempts(), 1500);
      })
      .catch(() => {
        this.setState({ retryingId: null });
        message.error('Retry failed to queue.');
      });
  }

  renderTables() {
    const {
      brokerHealth, publishAttempts, summary, summaryInFlight, violations, violationsInFlight, filters, runStatus,
      runHistory, writebackHistory
    } = this.props;
    const { page, retryingId, showOlderFailures } = this.state;
    const attempts = publishAttempts.results || [];
    const recentAttempts = attempts.filter(record => !record.is_older_failure);
    const olderFailures = attempts.filter(record => record.is_older_failure);
    const logoStyle = {
      height: '1.4em', width: 'auto', marginRight: 8, verticalAlign: 'middle'
    };
    const runState = effectiveRunState(runStatus);
    const runMeta = runStateLabel(runState);
    const activeRuns = runStatus.activeRuns || [];
    const activeRun = activeRuns[0] || null;
    const latestTerminal = (runStatus.recentTerminalRuns || [])[0];
    const terminalTimestamp = latestTerminal ? latestTerminal.receivedAt : null;
    const runTimestamp = activeRun ? activeRun.triggeredAt : terminalTimestamp;
    const terminalDetail = latestTerminal
      ? (latestTerminal.message || latestTerminal.rawStatus)
      : null;
    const runDetail = activeRun
      ? (activeRun.runName || activeRun.runIdentifier)
      : terminalDetail;
    const latestWriteback = (writebackHistory.results || [])[0] || null;
    const writebackMeta = writebackStateLabel(latestWriteback ? latestWriteback.status : null);
    const brokerMeta = brokerStateLabel(brokerHealth.state);
    const warningCount = summary.warnings && summary.warnings.count > 0
      ? summary.warnings.count
      : 0;
    const contractDetail = summary.contractVersion ? `Contract ${summary.contractVersion}` : null;
    const readinessDetail = warningCount > 0
      ? `${warningCount} ${warningCount === 1 ? 'user' : 'users'} with expiring tags`
      : contractDetail;
    return (
      <div className="bhp-optimiser-status__tables">
        <div className="bhp-optimiser-status__header">
          <h2>
            <img src={CROLogo} alt="Optimiser" style={logoStyle} />
            Optimiser Status
          </h2>
        </div>

        <div className="bhp-optimiser-status__tiles">
          <StatusTile
            label="Optimiser run"
            icon={<OptimiserRunIcon state={runState} size={18} />}
            state={runMeta.label}
            colour={runMeta.colour}
            detail={runDetail}
            timestamp={runTimestamp ? moment(runTimestamp).format('DD MMM YYYY HH:mm') : null}
          />
          <StatusTile
            label="Roster updates"
            state={writebackMeta.label}
            colour={writebackMeta.colour}
            detail={latestWriteback && latestWriteback.status === 'applied'
              ? `${appliedSummary(latestWriteback)}${latestWriteback.smsEnqueuedCount
                ? ` · ${latestWriteback.smsEnqueuedCount} SMS` : ''}`
              : null}
            timestamp={latestWriteback && latestWriteback.receivedAt
              ? moment(latestWriteback.receivedAt).format('DD MMM YYYY HH:mm') : null}
          />
          <StatusTile
            label="Broker"
            extra={(
              <Tooltip title="Broker is the computer that sends information from CrewRostering to the Optimiser. A healthy status means the Broker is able to receive messages from CrewRostering to send to the Optimiser, any other status indicates information may not be reaching the Optimiser.">
                <Icon type="info-circle" style={{ cursor: 'help' }} />
              </Tooltip>
            )}
            state={brokerMeta.label}
            colour={brokerMeta.colour}
            detail={brokerHealth.lastCheckAt
              ? `Checked ${moment(brokerHealth.lastCheckAt).format('DD MMM YYYY HH:mm')}` : null}
          />
          <StatusTile
            label="Data readiness"
            state={summary.total === 0 ? 'Ready' : `${summary.total} failing ${summary.total === 1 ? 'record' : 'records'}`}
            colour={summary.total === 0 ? '#389e0d' : '#cf1322'}
            detail={readinessDetail}
          />
        </div>

        {activeRuns.length > 1 && (
          <div className="bhp-optimiser-status__active-runs">
            {activeRuns.map(run => (
              <div
                key={run.runIdentifier}
                style={{
                  display: 'flex', alignItems: 'center', marginTop: 2, color: 'rgba(0,0,0,0.65)'
                }}
              >
                <OptimiserRunIcon state={run.state} size={14} />
                <span style={{ marginLeft: 6 }}>{run.runName || run.runIdentifier}</span>
                <span style={{ marginLeft: 4, color: runStateLabel(run.state).colour }}>
                  &middot;
                  {' '}
                  {runStateLabel(run.state).label}
                </span>
                {run.triggeredAt ? (
                  <span style={{ marginLeft: 4 }}>
                    &middot;
                    {' '}
                    {moment(run.triggeredAt).format('DD MMM YYYY HH:mm')}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {!summary.optimiserRoster && (
          <Alert
            type="warning"
            showIcon
            message="No roster selected for the Optimiser"
            description="Assignments will not be exported to the Optimiser until a roster is selected for use. Select one from the Rosters page."
            style={{ marginBottom: 16 }}
          />
        )}

        <CollapsibleSection
          sectionKey="writebackHistory"
          title="Roster updates from the optimiser"
          className="bhp-optimiser-status__writebacks"
        >
          <Table
            rowKey={record => record.id}
            loading={writebackHistory.inFlight && writebackHistory.results.length === 0}
            dataSource={writebackHistory.results}
            columns={buildWritebackColumns()}
            pagination={false}
            size="small"
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionKey="runHistory"
          title="Recent optimiser runs"
          className="bhp-optimiser-status__run-history"
        >
          <Table
            rowKey={record => record.id}
            loading={runHistory.inFlight && runHistory.results.length === 0}
            dataSource={runHistory.results}
            columns={buildRunHistoryColumns()}
            pagination={false}
            size="small"
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionKey="publishAttempts"
          title="Recent publish attempts"
          className="bhp-optimiser-status__attempts"
        >
          <Table
            rowKey={record => record.id}
            loading={publishAttempts.inFlight}
            dataSource={recentAttempts}
            columns={buildAttemptColumns(this.handleRetry, retryingId)}
            onRow={record => ({ onClick: () => this.handleAttemptClick(record) })}
            pagination={false}
            size="small"
          />
          {olderFailures.length > 0 && (
            <div className="bhp-optimiser-status__older-failures">
              <Button
                type="link"
                size="small"
                className="bhp-optimiser-status__older-failures-toggle"
                onClick={this.handleToggleOlderFailures}
              >
                <Icon type={showOlderFailures ? 'down' : 'right'} />
                {`${olderFailures.length} older unresolved ${olderFailures.length === 1 ? 'failure' : 'failures'} still retryable`}
              </Button>
              {showOlderFailures && (
                <Table
                  rowKey={record => record.id}
                  rowClassName={() => 'bhp-optimiser-status__attempt-row--older'}
                  dataSource={olderFailures}
                  columns={buildAttemptColumns(this.handleRetry, retryingId)}
                  onRow={record => ({ onClick: () => this.handleAttemptClick(record) })}
                  pagination={false}
                  size="small"
                  showHeader={false}
                />
              )}
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          sectionKey="failuresByType"
          title="Failures by type"
          className="bhp-optimiser-status__summary"
        >
          <Table
            rowKey={record => `${record.record_type}-${record.error_type}`}
            loading={summaryInFlight}
            dataSource={summary.rows}
            columns={buildSummaryColumns(this.handleDrillDown)}
            pagination={false}
            size="small"
          />
        </CollapsibleSection>

        {summary.warnings && summary.warnings.count > 0 && (
          <CollapsibleSection
            sectionKey="expiringTags"
            title={`Expiring tags — ${summary.warnings.count} ${summary.warnings.count === 1 ? 'user' : 'users'}`}
            className="bhp-optimiser-status__warnings"
          >
            <Table
              rowKey={record => `${record.record_type}-${record.record_id}`}
              dataSource={summary.warnings.records}
              columns={buildWarningColumns()}
              pagination={false}
              size="small"
              onRow={record => ({ onClick: () => this.handleRowClick(record) })}
            />
          </CollapsibleSection>
        )}

        <CollapsibleSection
          sectionKey="failingRecords"
          title="Failing records"
          className="bhp-optimiser-status__records"
        >
          <div className="bhp-optimiser-status__filters">
            <Select
              allowClear
              placeholder="Record type"
              style={{ width: 160 }}
              value={filters.recordType || undefined}
              onChange={this.handleRecordTypeChange}
            >
              {RECORD_TYPE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <Select
              allowClear
              placeholder="Error type"
              style={{ width: 180 }}
              value={filters.errorType || undefined}
              onChange={this.handleErrorTypeChange}
            >
              {ERROR_TYPE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <Button onClick={this.handleClearFilters}>
              Clear
            </Button>
            <Button type="primary" onClick={this.handleDownload}>
              <Icon type="download" />
              Download CSV
            </Button>
            <Tooltip title="Refresh" placement="bottomRight">
              <Button className="refreshButton" shape="circle" size="small" icon="reload" loading={violationsInFlight} onClick={this.handleRefresh} />
            </Tooltip>
          </div>
          <Table
            rowKey={violationRowKey}
            loading={violationsInFlight}
            dataSource={violations.results}
            columns={buildViolationColumns()}
            onChange={this.handleTableChange}
            onRow={record => ({ onClick: () => this.handleRowClick(record) })}
            pagination={{
              current: page + 1,
              pageSize: PAGE_SIZE,
              total: violations.count
            }}
            size="small"
          />
        </CollapsibleSection>
      </div>
    );
  }

  render() {
    const { selectedRecord, selectedAttemptId } = this.state;
    return (
      <Layout
        classes="bhp-optimiser-status"
        component1={this.renderTables()}
        component2={selectedAttemptId ? (
          <ConnectedPublishAttemptDetail
            attemptId={selectedAttemptId}
            onResend={this.handleRetry}
            onClose={this.handleAttemptClose}
          />
        ) : (
          <ConnectedRecordDetailHost
            recordType={selectedRecord ? selectedRecord.recordType : null}
            recordId={selectedRecord ? selectedRecord.recordId : null}
            onSaved={this.handleRecordSaved}
            onClose={this.handleRecordClose}
          />
        )}
      />
    );
  }
}

OptimiserStatusPage.propTypes = {
  brokerHealth: PropTypes.shape({ state: PropTypes.string }),
  publishAttempts: PropTypes.shape({ results: PropTypes.array, inFlight: PropTypes.bool }),
  summary: PropTypes.object,
  summaryInFlight: PropTypes.bool,
  violations: PropTypes.object,
  violationsInFlight: PropTypes.bool,
  filters: PropTypes.object,
  runStatus: PropTypes.shape({
    state: PropTypes.string,
    activeRuns: PropTypes.array,
    recentTerminalRuns: PropTypes.array,
    finishedWindowSeconds: PropTypes.number
  }),
  runHistory: PropTypes.shape({
    results: PropTypes.array,
    inFlight: PropTypes.bool
  }),
  writebackHistory: PropTypes.shape({
    results: PropTypes.array,
    inFlight: PropTypes.bool
  }),
  loadRunHistory: PropTypes.func,
  loadWritebackHistory: PropTypes.func,
  loadBrokerHealth: PropTypes.func,
  loadPublishAttempts: PropTypes.func,
  loadSummary: PropTypes.func,
  loadViolations: PropTypes.func,
  loadRunStatus: PropTypes.func,
  retryPublish: PropTypes.func,
  setFilters: PropTypes.func,
  downloadCsv: PropTypes.func
};

OptimiserStatusPage.defaultProps = {
  brokerHealth: { state: 'unknown' },
  publishAttempts: { results: [], inFlight: false },
  summary: {
    tenancyId: null,
    contractVersion: null,
    total: 0,
    rows: [],
    optimiserRoster: null,
    warnings: { count: 0, records: [] }
  },
  summaryInFlight: false,
  violations: {
    results: [],
    count: 0
  },
  violationsInFlight: false,
  filters: {
    recordType: null,
    errorType: null
  },
  runStatus: { state: 'unknown', activeRuns: [], recentTerminalRuns: [] },
  runHistory: { results: [], inFlight: false, error: null },
  writebackHistory: { results: [], inFlight: false, error: null },
  loadRunHistory() {},
  loadWritebackHistory() {},
  loadBrokerHealth() {},
  loadPublishAttempts() {},
  loadSummary() {},
  loadViolations() {},
  loadRunStatus() {},
  retryPublish() {},
  setFilters() {},
  downloadCsv() {}
};

const mapStateToProps = state => ({
  brokerHealth: state.solaceHealth ? state.solaceHealth.health : { state: 'unknown' },
  publishAttempts: state.optimiserStatus ? state.optimiserStatus.publishAttempts : { results: [], inFlight: false },
  summary: state.optimiserStatus.summary,
  summaryInFlight: state.optimiserStatus.summaryInFlight,
  violations: state.optimiserStatus.violations,
  violationsInFlight: state.optimiserStatus.violationsInFlight,
  filters: state.optimiserStatus.filters,
  runStatus: state.optimiserRunStatus ? state.optimiserRunStatus.runStatus : { state: 'unknown', activeRuns: [], recentTerminalRuns: [] },
  runHistory: state.optimiserRunStatus ? state.optimiserRunStatus.runHistory : { results: [], inFlight: false, error: null },
  writebackHistory: state.optimiserStatus ? state.optimiserStatus.writebackHistory : { results: [], inFlight: false, error: null }
});

const mapDispatchToProps = dispatch => ({
  loadBrokerHealth() {
    return dispatch(loadBrokerHealthAction());
  },
  loadPublishAttempts() {
    return dispatch(loadPublishAttemptsAction());
  },
  loadSummary() {
    return dispatch(loadSummaryAction());
  },
  loadViolations(props) {
    return dispatch(loadViolationsAction(props));
  },
  loadRunStatus() {
    return dispatch(loadRunStatusAction());
  },
  loadRunHistory() {
    return dispatch(loadRunHistoryAction());
  },
  loadWritebackHistory() {
    return dispatch(loadWritebackHistoryAction());
  },
  retryPublish(id) {
    return dispatch(retryPublishAction(id));
  },
  setFilters(filters) {
    return dispatch(setFiltersAction(filters));
  },
  downloadCsv(filters) {
    return dispatch(downloadCsvAction(filters));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OptimiserStatusPage);
